import { useState, useEffect } from "react";
import { MainLayout } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { dashboardAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Wrench, 
  Users, 
  DollarSign, 
  Star, 
  AlertTriangle,
  Clock,
  CheckCircle2,
  TrendingUp,
  Package,
  Bell,
  ChevronRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const STATUSES = ["reception", "diagnostic", "pending_parts", "in_repair", "testing", "ready", "delivered"];

function AlertItem({ alert, onClick }) {
  let colorClass = "bg-blue-500/10 border-blue-500/30 text-blue-500";
  if (alert.severity === "danger") {
    colorClass = "bg-red-500/10 border-red-500/30 text-red-500";
  } else if (alert.severity === "warning") {
    colorClass = "bg-amber-500/10 border-amber-500/30 text-amber-500";
  }
  
  return (
    <div 
      className={`flex items-center justify-between p-3 rounded-md border cursor-pointer hover:opacity-80 ${colorClass}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-foreground">{alert.title}</p>
          <p className="text-xs text-muted-foreground">{alert.message}</p>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </div>
  );
}

function StatCard({ title, value, subtitle, icon, colorClass }) {
  return (
    <Card className="bg-card border-border card-hover">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs sm:text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl sm:text-3xl font-light font-chivo text-foreground mt-1">{value}</p>
            {subtitle && <p className="text-xs text-emerald-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-10 h-10 sm:w-12 sm:h-12 ${colorClass} rounded-md flex items-center justify-center`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SimpleBarChart({ data }) {
  if (!data || data.length === 0) {
    return <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">Pas encore de données</div>;
  }
  
  const maxValue = Math.max(...data.map(function(d) { return d.total; }));
  
  return (
    <div className="h-48 flex items-end gap-2 px-4">
      {data.map(function(item) {
        const height = maxValue > 0 ? (item.total / maxValue) * 100 : 0;
        return (
          <div key={item._id} className="flex-1 flex flex-col items-center">
            <div 
              className="w-full bg-emerald-500 rounded-t-sm transition-all duration-300"
              style={{ height: height + "%" }}
              title={item.total.toFixed(0) + "€"}
            />
            <span className="text-xs text-muted-foreground mt-2">{item._id.slice(5)}</span>
          </div>
        );
      })}
    </div>
  );
}

function StatusPieChart({ data }) {
  if (!data || data.length === 0) {
    return <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">Pas encore de données</div>;
  }
  
  const colors = ["#22c55e", "#3b82f6", "#f59e0b", "#8b5cf6", "#06b6d4", "#10b981", "#6b7280"];
  const total = data.reduce(function(sum, item) { return sum + item.count; }, 0);
  
  return (
    <div className="space-y-2">
      {data.map(function(item, index) {
        const percent = total > 0 ? (item.count / total) * 100 : 0;
        return (
          <div key={item.status} className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: colors[index % colors.length] }} />
            <span className="text-sm text-muted-foreground flex-1 truncate">{item.status}</span>
            <div className="w-20 h-2 bg-secondary rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: percent + "%", background: colors[index % colors.length] }} />
            </div>
            <span className="text-sm text-foreground font-medium w-8 text-right">{item.count}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [advancedStats, setAdvancedStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(function() {
    async function fetchData() {
      try {
        const statsRes = await dashboardAPI.stats();
        setStats(statsRes.data);
        
        const advancedRes = await dashboardAPI.advancedStats();
        setAdvancedStats(advancedRes.data);
        
        const alertsRes = await dashboardAPI.alerts();
        setAlerts(alertsRes.data);
      } catch (err) {
        console.error("Error fetching stats:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  function handleAlertClick(alert) {
    if (alert.entity_type === "repair") {
      navigate("/repairs/" + alert.entity_id);
    } else if (alert.entity_type === "part") {
      navigate("/inventory");
    } else if (alert.entity_type === "invoice") {
      navigate("/invoices");
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Chargement...</div>
        </div>
      </MainLayout>
    );
  }

  const statusCounts = stats ? stats.status_counts : {};
  const recentRepairs = stats ? stats.recent_repairs : [];
  const monthlyRevenue = advancedStats ? advancedStats.monthly_revenue : [];
  const statusData = advancedStats ? advancedStats.repairs_by_status : [];
  const topClients = advancedStats ? advancedStats.top_clients : [];
  const topBrands = advancedStats ? advancedStats.repairs_by_brand : [];

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="dashboard">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-chivo text-2xl sm:text-3xl font-bold text-foreground">Tableau de bord</h1>
            <p className="text-muted-foreground text-sm mt-1">Vue d'ensemble de votre activité</p>
          </div>
          {alerts.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 rounded-md border border-amber-500/30">
              <Bell className="w-4 h-4 text-amber-500" />
              <span className="text-sm text-amber-500 font-medium">{alerts.length} alerte(s)</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard 
            title="Réparations actives" 
            value={stats ? stats.active_repairs : 0}
            icon={<Wrench className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />}
            colorClass="bg-primary/20"
          />
          <StatCard 
            title="Aujourd'hui" 
            value={advancedStats ? advancedStats.today_repairs : 0}
            subtitle={"+" + (advancedStats ? advancedStats.today_revenue.toFixed(0) : 0) + "€"}
            icon={<TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" />}
            colorClass="bg-emerald-500/20"
          />
          <StatCard 
            title="Chiffre d'affaires" 
            value={(stats ? stats.total_revenue.toFixed(0) : 0) + "€"}
            icon={<DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />}
            colorClass="bg-blue-500/20"
          />
          <StatCard 
            title="Note moyenne" 
            value={(advancedStats ? advancedStats.avg_rating : 0) + "/5"}
            subtitle={(advancedStats ? advancedStats.review_count : 0) + " avis"}
            icon={<Star className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />}
            colorClass="bg-amber-500/20"
          />
        </div>

        {alerts.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-chivo flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-500" />
                Alertes ({alerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {alerts.slice(0, 5).map(function(alert) {
                  return <AlertItem key={alert.id} alert={alert} onClick={function() { handleAlertClick(alert); }} />;
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-chivo">Revenus mensuels</CardTitle>
            </CardHeader>
            <CardContent>
              <SimpleBarChart data={monthlyRevenue} />
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-chivo">Réparations par statut</CardTitle>
            </CardHeader>
            <CardContent>
              <StatusPieChart data={statusData} />
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-chivo flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Top clients
              </CardTitle>
            </CardHeader>
            <CardContent>
              {topClients.length > 0 ? (
                <div className="space-y-3">
                  {topClients.map(function(client, index) {
                    return (
                      <div key={client._id} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </div>
                          <span className="text-sm text-foreground truncate max-w-[120px]">{client.client_name}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-foreground">{client.count} répar.</p>
                          <p className="text-xs text-muted-foreground">{(client.total_spent || 0).toFixed(0)}€</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-muted-foreground text-sm py-8">Aucun client</p>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-chivo flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-500" />
                Marques réparées
              </CardTitle>
            </CardHeader>
            <CardContent>
              {topBrands.length > 0 ? (
                <div className="space-y-2">
                  {topBrands.slice(0, 5).map(function(brand) {
                    const maxCount = topBrands[0] ? topBrands[0].count : 1;
                    const percent = (brand.count / maxCount) * 100;
                    return (
                      <div key={brand._id} className="flex items-center justify-between">
                        <span className="text-sm text-foreground">{brand._id || "Inconnu"}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-secondary rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: percent + "%" }} />
                          </div>
                          <span className="text-sm text-muted-foreground w-8 text-right">{brand.count}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-muted-foreground text-sm py-8">Aucune donnée</p>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-chivo flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-500" />
                Statistiques
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-md">
                <span className="text-sm text-muted-foreground">Temps moyen</span>
                <span className="text-sm font-medium text-foreground">{advancedStats ? advancedStats.avg_repair_days : 0} jours</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-md">
                <span className="text-sm text-muted-foreground">Total clients</span>
                <span className="text-sm font-medium text-foreground">{stats ? stats.total_clients : 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-md">
                <span className="text-sm text-muted-foreground">Terminées</span>
                <span className="text-sm font-medium text-foreground">{stats ? stats.completed_repairs : 0}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-card border-border">
          <CardHeader className="pb-4 flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-chivo">Réparations récentes</CardTitle>
            <button 
              onClick={function() { navigate("/repairs"); }}
              className="text-sm text-primary hover:underline"
              data-testid="view-all-repairs-btn"
            >
              Voir tout
            </button>
          </CardHeader>
          <CardContent>
            {recentRepairs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Ticket</th>
                      <th className="hidden sm:table-cell">Client</th>
                      <th>Appareil</th>
                      <th>Statut</th>
                      <th className="hidden sm:table-cell">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRepairs.map(function(repair) {
                      return (
                        <tr 
                          key={repair.id} 
                          className="cursor-pointer"
                          onClick={function() { navigate("/repairs/" + repair.id); }}
                          data-testid={"repair-row-" + repair.id}
                        >
                          <td className="font-mono text-sm text-primary">{repair.ticket_number}</td>
                          <td className="hidden sm:table-cell">{repair.client_name}</td>
                          <td>{repair.device_brand} {repair.device_model}</td>
                          <td><StatusBadge status={repair.status} /></td>
                          <td className="hidden sm:table-cell text-muted-foreground text-sm">
                            {new Date(repair.created_at).toLocaleDateString("fr-FR")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucune réparation pour le moment</p>
                <button 
                  onClick={function() { navigate("/repairs"); }}
                  className="text-primary hover:underline mt-2"
                >
                  Créer une première réparation
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
